import React, {useEffect, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import classNames from 'classnames';

import TextInput from 'src/components/inputs/text-input';
import Icon from 'shared/components/icon';

import {receiveFeatureFlags, deleteFeatureFlag} from 'src/redux/admin/actions/feature-flags';
import {AdminStore} from 'src/types/store';

import SimpleTable from 'src/components/SimpleTable';
import AdminFeatureFlagService from 'src/services/admin-feature-flag';
import Button from 'src/components/button';
import IconTooltip from 'shared/components/icon-tooltip';
import styles from './styles.module.scss';

// Returns true if every character in `query` appears in `str` in order (not necessarily
// contiguous). For example, isSubsequenceMatch("cSS", "collegeSearchShare") is true.
// Very similar to how the JetBrains IDEs do fuzzy matching in their file search.
export const isSubsequenceMatch = (query: string, str: string) => {
  const lowerQuery = query.toLowerCase();
  const lowerStr = str.toLowerCase();
  let queryIndex = 0;
  for (let i = 0; i < lowerStr.length && queryIndex < lowerQuery.length; i++) {
    if (lowerStr[i] === lowerQuery[queryIndex]) queryIndex++;
  }
  return queryIndex === lowerQuery.length;
};

const FeatureFlags = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {pathname} = useLocation();
  const features = useSelector((state: AdminStore) => Object.values(state.featureFlags));
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFeatures = (() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return features;

    const exactMatches: typeof features = [];
    const subsequenceMatches: typeof features = [];

    for (const feature of features) {
      const name = feature.name.toLowerCase();
      const description = (feature.description ?? '').toLowerCase();
      if (name.includes(query) || description.includes(query)) {
        exactMatches.push(feature);
      } else if (isSubsequenceMatch(query, feature.name)) {
        subsequenceMatches.push(feature);
      }
    }

    return [...exactMatches, ...subsequenceMatches];
  })();

  useEffect(() => {
    AdminFeatureFlagService.index().then((response) => {
      dispatch(receiveFeatureFlags(response));
    });
  }, [dispatch]);

  return (
    <div className="mb-24 ml-64 mr-64">
      <h1 className="shared-font headline bold">Feature Flags</h1>
      <p className="shared-font body bold">Usage Notes:</p>
      <ul className="shared-font body">
        <li>
          Percentage is 0-100, integers only. A feature is considered &quot;globally active&quot;
          when Percentage is 100.
        </li>
        <li>
          Wrt Percentage, the algorithm for determining which users get let in is this:
          <br />
          <pre>{'CRC32(user.id) % 100_000 < percentage * 1_000'}</pre>
          So, for 20%, users 0, 1, 10, 11, 20, 21, etc would be allowed in. Those users would remain
          in as the percentage increases.
          <br />
          That&apos;s not really random at all.
          <br />
          In some cases you might want to have a feature activated for a random set of users. It can
          come specially handy when using Rollout for split tests.
          <br />
          <pre>$rollout = Rollout.new($redis, randomize_percentage: true)</pre>
          We might want to consider this.
        </li>
        <li>
          <strong>Users</strong> activates the feature for the specified user id&apos;s. Set
          percentage to 0% unless you also want unspecified users to have this feature activated.
        </li>
        <li>
          <strong>Groups</strong> activates the feature for the specified groups. (Currently defined
          groups include <code>students</code>, <code>demo_students</code>,{' '}
          <code>non_demo_students</code>, <code>high_school_students</code>,{' '}
          <code>community_college_students</code>, <code>undergraduate_students</code>,{' '}
          <code>educators</code>, and
          <code>college_portal_users</code>)
        </li>
        <br />
        <li>
          PMs, please create a ticket to remove dead code related to a feature flag once it&apos;s
          been activated and happily chugging along in prod. Once the code is removed (jira ticket
          complete), please come back here and delete the flag using this interface.
        </li>
      </ul>

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <TextInput
            type="search"
            placeholder="Search feature flags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="medium"
          >
            <Icon iconType="search" className={styles.searchIcon} />
          </TextInput>
        </div>
        <Button
          onClick={() =>
            history.replace({
              pathname: `${pathname}/new`,
              state: {skipScrolling: true},
            })
          }
        >
          Add New Feature
        </Button>
      </div>
      {searchQuery && features.length !== filteredFeatures.length && (
        <p className={classNames('shared-font body', styles.hiddenCount)}>
          Showing {filteredFeatures.length} of {features.length} flags
        </p>
      )}

      <SimpleTable>
        <SimpleTable.Header
          columns={[
            {value: 'Name'},
            {value: 'Description'},
            {value: 'Percentage'},
            {value: 'Users'},
            {value: 'Groups'},
            {value: 'Latest Author'},
            {value: 'Created At'},
            {value: 'Updated At'},
            {value: 'Actions'},
          ]}
        />
        <SimpleTable.Body
          rows={filteredFeatures.map((feature) => {
            let status = 'partially released';

            if (feature.percentage === 100) {
              status = 'fully released';
            } else if (
              feature.percentage === 0 &&
              !feature.users.length &&
              !feature.groups.length
            ) {
              status = 'off';
            }

            return [
              {
                value: (
                  <div
                    className={classNames(styles.name, {
                      [styles.released]: status === 'fully released',
                      [styles.partial]: status === 'partially released',
                      [styles.off]: status === 'off',
                    })}
                    title={`Feature ${status}`}
                  >
                    <code>{feature.name}</code>
                  </div>
                ),
              },
              {value: feature.description},
              {value: feature.percentage},
              {
                value:
                  feature.usersListDisplay.length > 0 ? (
                    <div className={styles.users}>{feature.usersListDisplay.join('\n')}</div>
                  ) : (
                    ''
                  ),
              },
              {
                value:
                  feature.groups.length > 0
                    ? feature.groups.map((group) => (
                        <code key={`${feature.id}-${group}`} className={styles.code}>
                          {group}
                        </code>
                      ))
                    : '',
              },
              {value: feature.history.at(-1)?.author},
              {value: feature.createdAt},
              {value: feature.updatedAt},
              {
                value: (
                  <>
                    <IconTooltip
                      iconType="pen"
                      iconSize={15}
                      onClick={() =>
                        history.replace({
                          pathname: `${pathname}/${feature.id}`,
                          state: {skipScrolling: true},
                        })
                      }
                      placement="top"
                    >
                      Edit feature flag
                    </IconTooltip>
                    <IconTooltip
                      className={styles.delete}
                      iconType="trash"
                      iconSize={15}
                      onClick={async () => {
                        if (confirm('Are you sure?')) {
                          await AdminFeatureFlagService.destroy(feature.id);
                          dispatch(deleteFeatureFlag(feature.id));
                        }
                      }}
                      placement="top"
                    >
                      Delete feature flag
                    </IconTooltip>
                  </>
                ),
              },
            ];
          })}
        />
      </SimpleTable>
    </div>
  );
};

export default FeatureFlags;
